import './ServiceOrdersList.scss';

import { CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { DateField, List, ShowButton, useTable } from '@refinedev/antd';
import { BaseRecord } from '@refinedev/core';
import { Flex, Space } from 'antd';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppTable from '@/components/general/AppTable';
import LinkTextField from '@/components/general/LinkTextField';
import ProtectedCreateButton from '@/components/protected/ProtectedCreateButton';
import ServiceOrderStatusTag from '@/components/serviceOrder/ServiceOrderStatusTag';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUiContext } from '@/contexts/UiContext';
import { useUserContext } from '@/contexts/UserContext';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import {
  SERVICE_ORDER_STATUS,
  SERVICE_ORDER_TYPE,
  ServiceOrder,
} from '@/model/ServiceOrder/ServiceOrder';
import { RESOURCES } from '@/Resources';

import ServiceOrderFilter from './ServiceOrderFilter/ServiceOrderFilter';

const ServiceOrdersList = () => {
  const { pageSizePreference } = useUserContext();
  const { userWorkArea } = useUserContext();
  const { hasPermission } = usePermissionContext();
  const { isMediumScreen } = useUiContext();
  const { t } = useTranslation();
  const [isReady, setIsReady] = useState(false);
  const {
    tableProps,
    filters,
    setFilters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    sorters,
    setSorters,
  } = useTable<ServiceOrder>({
    resource: 'service_orders',
    sorters: {
      initial: [{ field: 'id', order: 'desc' }],
      mode: 'server',
    },
    syncWithLocation: true,
    queryOptions: {
      refetchInterval: 300000, // 5 minutes
      retry: 3,
      enabled: isReady,
    },
    pagination: {
      pageSize: pageSizePreference,
    },
  });

  useListStatePersistence({
    resource: RESOURCES.SERVICE_ORDERS,
    filters,
    setFilters,
    sorters,
    setSorters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    onReady: () => setIsReady(true),
  });

  return (
    <List
      headerButtons={({ createButtonProps }) => (
        <>
          <ServiceOrderFilter filters={filters} setFilters={setFilters} setCurrent={setCurrent} />
          <ProtectedCreateButton {...createButtonProps} />
        </>
      )}
    >
      <AppTable<ServiceOrder>
        {...tableProps}
        dataSource={(tableProps.dataSource as ServiceOrder[])?.sort((a, b) => b.id - a.id) ?? []}
        rowKey={'id'}
        rowClassName={record => {
          const status: SERVICE_ORDER_STATUS = record.status;

          return `TableRowServiceOrder ${
            status == SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION
              ? 'OpenServiceOrder'
              : status == SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION
                ? 'InProcessServiceOrder'
                : status == SERVICE_ORDER_STATUS.FINALIZADA
                  ? 'CompletedServiceOrder'
                  : status == SERVICE_ORDER_STATUS.CANCELADO
                    ? 'CancelledServiceOrder'
                    : ''
          }`;
        }}
        scroll={{ y: 'calc(100dvh - 310px)', x: 1500 }}
      >
        <AppTable.Column
          dataIndex="id"
          title={'Id'}
          width={50}
          render={value =>
            hasPermission('show') ? (
              <LinkTextField
                label={value}
                to={`/${RESOURCES.SERVICE_ORDERS}/show/${value}`}
                local
                withoutColor
              />
            ) : (
              value
            )
          }
        />
        <AppTable.Column
          width={160}
          dataIndex="technician"
          title={t('Técnico')}
          render={value => <span>{value.nombre}</span>}
        />
        <AppTable.Column
          width={160}
          dataIndex="company"
          title={t('Empresa')}
          render={value => <span>{value.full_name}</span>}
        />
        <AppTable.Column
          width={80}
          dataIndex="tickets"
          title={t('Tickets')}
          render={value => {
            const tickets: ReactNode[] = value.map(ticket => (
              <div key={`${ticket}`}>
                <LinkTextField
                  to={`/${RESOURCES.TICKETS}/show/${ticket}`}
                  label={ticket}
                  local
                  withoutColor
                />
              </div>
            ));

            return <span>{tickets}</span>;
          }}
        />
        <AppTable.Column
          width={160}
          dataIndex="type"
          title={t('Tipo de servicio')}
          render={value => (
            <span>
              {value == SERVICE_ORDER_TYPE.INSTALACION ? t('Instalación') : t('Servicio técnico')}
            </span>
          )}
        />

        <AppTable.Column
          width={120}
          dataIndex="service_date"
          title={t('Fecha del servicio')}
          render={value => <DateField value={value} format="L" />}
        />
        {userWorkArea == USER_WORK_AREA.LOGISTICS && (
          <AppTable.Column
            title={t('Stock revisado')}
            width={100}
            dataIndex="stock_technician_reviewed"
            render={value => (
              <Flex align="center" justify="center">
                {value ? (
                  <CheckCircleFilled className="TechnicianStockSuccess" />
                ) : (
                  <WarningFilled className="TechnicianStockWarning" />
                )}
              </Flex>
            )}
          />
        )}
        <AppTable.Column
          fixed={!isMediumScreen ? 'right' : undefined}
          width={140}
          dataIndex="status"
          render={value => (
            <Flex align="center" justify="center">
              <ServiceOrderStatusTag serviceOrderStatus={value} />
            </Flex>
          )}
        />
        {hasPermission('show') && (
          <AppTable.Column
            dataIndex="actions"
            width={30}
            fixed="right"
            render={(_, record: BaseRecord) => (
              <Space>
                <ShowButton hideText size="small" recordItemId={record.id} />
              </Space>
            )}
          />
        )}
      </AppTable>
    </List>
  );
};

export default ServiceOrdersList;
